import {match, Pattern} from 'ts-pattern';

import type {DataGridRow} from '@dms/api/core';

import type {CustomerInsightsEntitlements} from '../hooks/useCustomerInsightsEntitlements';
import type {CustomerInsightsGridQueryResult} from '../hooks/useCustomerInsightsGridQuery';
import type {
  CustomerInsightsAlert,
  CustomerInsightsHistoryGroup,
  CustomerInsightsRecord,
  CustomerInsightsSection,
} from '../types/CustomerInsightsViewModel';
import {buildSectionState} from './buildSectionState';
import {combineCustomerInsightsHistorySections} from './combineCustomerInsightsHistorySections';
import {getBusinessCaseRowType} from './getBusinessCaseRowType';
import {getCustomerInsightsBusinessCaseCategory} from './getCustomerInsightsBusinessCaseCategory';
import {getCustomerInsightsEntitledQueryState} from './getCustomerInsightsEntitledQueryState';
import {mapBusinessCaseHistory} from './mapBusinessCaseHistory';
import {mapCustomerInsightsAlert} from './mapCustomerInsightsAlert';
import {mapDataGridRow} from './mapDataGridRow';
import {takeNewestHistory} from './takeNewestHistory';

interface CustomerInsightsSectionPolicyArgs {
  enabled: boolean;
  entitlements: CustomerInsightsEntitlements;
  expandCombinedHistoryRowsLimit: VoidFunction;
  resetCombinedHistoryRowsLimit: VoidFunction;
}

type CustomerInsightsBusinessCaseHistoryKind = 'sales' | 'purchases';

export interface CustomerInsightsSectionSource {
  query: CustomerInsightsGridQueryResult;
  kind: CustomerInsightsRecord['kind'];
  entitled: boolean;
}

type CustomerInsightsHistorySource = CustomerInsightsSectionSource &
  (
    | {kind: 'businessCase'; businessCaseHistoryKind?: CustomerInsightsBusinessCaseHistoryKind}
    | {kind: 'interest' | 'serviceCase'; businessCaseHistoryKind?: never}
  );

interface CustomerInsightsAlertSource {
  query: CustomerInsightsGridQueryResult;
  kind: CustomerInsightsAlert['kind'];
  href: string | undefined;
  additionalItemsHref: string;
  entitled: boolean;
}

interface CustomerInsightsCombinedHistorySource {
  first: CustomerInsightsHistorySource;
  second: CustomerInsightsHistorySource;
  requestedRowsLimit: number;
}

export interface CustomerInsightsSectionPolicy {
  records: (
    _source: CustomerInsightsSectionSource
  ) => CustomerInsightsSection<CustomerInsightsRecord[]>;
  history: (
    _source: CustomerInsightsHistorySource
  ) => CustomerInsightsSection<CustomerInsightsHistoryGroup>;
  alert: (_source: CustomerInsightsAlertSource) => CustomerInsightsSection<CustomerInsightsAlert>;
  combinedHistory: (
    _source: CustomerInsightsCombinedHistorySource
  ) => CustomerInsightsSection<CustomerInsightsHistoryGroup>;
}

const isVisibleBusinessCaseRow = (
  row: DataGridRow,
  businessCaseSales: boolean,
  businessCasePurchases: boolean
) =>
  match(getCustomerInsightsBusinessCaseCategory(getBusinessCaseRowType(row)))
    .with('sale', () => businessCaseSales)
    .with('purchase', () => businessCasePurchases)
    .otherwise(() => businessCaseSales && businessCasePurchases);

const getVisibleRows = (
  rows: DataGridRow[],
  kind: CustomerInsightsRecord['kind'],
  businessCaseSales: boolean,
  businessCasePurchases: boolean
): DataGridRow[] =>
  match(kind)
    .with('businessCase', () =>
      rows.filter((row) => isVisibleBusinessCaseRow(row, businessCaseSales, businessCasePurchases))
    )
    .otherwise(() => rows);

const mapGridRows = (
  rows: DataGridRow[],
  kind: CustomerInsightsRecord['kind'],
  businessCaseSales: boolean,
  businessCasePurchases: boolean
): CustomerInsightsRecord[] =>
  getVisibleRows(rows, kind, businessCaseSales, businessCasePurchases).map((row) =>
    mapDataGridRow(row, kind)
  );

const withRecordPagination = (
  section: CustomerInsightsSection<CustomerInsightsRecord[]>,
  query: CustomerInsightsGridQueryResult
): CustomerInsightsSection<CustomerInsightsRecord[]> =>
  match(section)
    .with({status: 'ready'}, {status: 'degraded'}, (currentSection) =>
      query.isTotalAuthoritative && query.total !== undefined
        ? {
            ...currentSection,
            total: query.total,
            hasMore: query.hasMore,
            isPaginationExpanded: query.isPaginationExpanded,
            isLoadingMore: query.isLoadingMore,
            loadMore: query.loadMore,
            showLess: query.showLess,
          }
        : currentSection
    )
    .otherwise((currentSection) => currentSection);

const withPagination = (
  history: CustomerInsightsHistoryGroup,
  query: CustomerInsightsGridQueryResult
): CustomerInsightsHistoryGroup =>
  query.hasMore || query.isLoadingMore || query.isPaginationExpanded
    ? {
        ...history,
        hasMore: query.hasMore,
        isPaginationExpanded: query.isPaginationExpanded,
        isLoadingMore: query.isLoadingMore,
        loadMore: query.loadMore,
        showLess: query.showLess,
      }
    : history;

const getAuthoritativeTotal = (query: CustomerInsightsGridQueryResult) =>
  match(query)
    .with({isTotalAuthoritative: true, total: Pattern.number}, ({total}) => total)
    .otherwise(() => undefined);

const mapHistory = (
  query: CustomerInsightsGridQueryResult,
  kind: CustomerInsightsRecord['kind'],
  businessCaseSales: boolean,
  businessCasePurchases: boolean,
  businessCaseHistoryKind?: CustomerInsightsBusinessCaseHistoryKind
) =>
  withPagination(
    businessCaseHistoryKind === undefined
      ? takeNewestHistory(
          mapGridRows(query.rows, kind, businessCaseSales, businessCasePurchases),
          getAuthoritativeTotal(query),
          query.requestedRowsLimit
        )
      : mapBusinessCaseHistory(
          getVisibleRows(query.rows, kind, businessCaseSales, businessCasePurchases),
          getAuthoritativeTotal(query),
          businessCaseHistoryKind,
          query.requestedRowsLimit
        ),
    query
  );

export function createCustomerInsightsSectionPolicy(
  args: CustomerInsightsSectionPolicyArgs
): CustomerInsightsSectionPolicy {
  const getQueryState = (query: CustomerInsightsGridQueryResult, entitled: boolean) =>
    getCustomerInsightsEntitledQueryState({
      enabled: args.enabled,
      entitled,
      entitlementStatus: args.entitlements.status,
      entitlementRetry: args.entitlements.retry,
      isLoading: query.isLoading,
      isError: query.isError,
      retry: query.retry,
    });

  const records = (source: CustomerInsightsSectionSource) => {
    const section = buildSectionState({
      data: mapGridRows(
        source.query.rows,
        source.kind,
        args.entitlements.businessCaseSales,
        args.entitlements.businessCasePurchases
      ),
      isEmpty: (data) => data.length === 0,
      ...getQueryState(source.query, source.entitled),
    });

    return withRecordPagination(section, source.query);
  };

  const history = (source: CustomerInsightsHistorySource) =>
    buildSectionState({
      data: mapHistory(
        source.query,
        source.kind,
        args.entitlements.businessCaseSales,
        args.entitlements.businessCasePurchases,
        source.kind === 'businessCase' ? source.businessCaseHistoryKind : undefined
      ),
      isEmpty: (data) => data.items.length === 0,
      ...getQueryState(source.query, source.entitled),
    });

  const alert = (source: CustomerInsightsAlertSource) => {
    const alertData = mapCustomerInsightsAlert(source.query.rows[0], source.kind, source.href);
    const additionalCount = Math.max((source.query.total ?? 1) - 1, 0);

    return buildSectionState({
      data:
        alertData === undefined
          ? undefined
          : {
              ...alertData,
              ...(additionalCount > 0
                ? {additionalCount, additionalItemsHref: source.additionalItemsHref}
                : {}),
            },
      isEmpty: () => false,
      ...getQueryState(source.query, source.entitled),
    });
  };

  const combinedHistory = (source: CustomerInsightsCombinedHistorySource) =>
    combineCustomerInsightsHistorySections(
      history(source.first),
      history(source.second),
      source.requestedRowsLimit,
      args.expandCombinedHistoryRowsLimit,
      args.resetCombinedHistoryRowsLimit
    );

  return {records, history, alert, combinedHistory};
}
